import React, { useRef, useEffect, useState } from 'react';
import { Camera } from '@mediapipe/camera_utils';
import { SelfieSegmentation } from '@mediapipe/selfie_segmentation';

interface BackgroundBlurVideoProps {
  onLoad?: () => void;
  onError?: (error: Error) => void;
  blurAmount?: number;
  className?: string;
  width?: number;
  height?: number;
  mirrored?: boolean;
}

export const BackgroundBlurVideo: React.FC<BackgroundBlurVideoProps> = ({
  onLoad,
  onError,
  blurAmount = 15,
  className = '',
  width = 640,
  height = 480,
  mirrored = true
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let camera: Camera | null = null;
    let selfieSegmentation: SelfieSegmentation | null = null;

    const initializeBlur = async () => {
      try {
        if (!videoRef.current || !canvasRef.current) return;

        const videoElement = videoRef.current;
        const canvasElement = canvasRef.current;
        const canvasCtx = canvasElement.getContext('2d');

        if (!canvasCtx) {
          throw new Error("Could not get canvas context");
        }

        // Set up the selfie segmentation model
        selfieSegmentation = new SelfieSegmentation({
          locateFile: (file) => {
            return `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`;
          }
        });

        selfieSegmentation.setOptions({
          modelSelection: 1, // 0 for general model, 1 for landscape model (better performance)
        });

        // Process results
        selfieSegmentation.onResults((results) => {
          if (!canvasCtx || !canvasElement) return;
          
          // Save the canvas state
          canvasCtx.save();
          canvasCtx.clearRect(0, 0, canvasElement.width, canvasElement.height);
          
          // Apply mirror effect via canvas transformation if needed
          if (mirrored) {
            canvasCtx.translate(canvasElement.width, 0);
            canvasCtx.scale(-1, 1);
          }
          
          // Draw the segmentation mask
          canvasCtx.drawImage(
            results.segmentationMask,
            0, 0,
            canvasElement.width, canvasElement.height
          );
          
          // Only keep the foreground (person)
          canvasCtx.globalCompositeOperation = 'source-in';
          canvasCtx.drawImage(
            results.image,
            0, 0,
            canvasElement.width, canvasElement.height
          );
          
          // Now draw the background with blur
          canvasCtx.globalCompositeOperation = 'destination-atop';
          canvasCtx.filter = `blur(${blurAmount}px)`;
          canvasCtx.drawImage(
            results.image,
            0, 0,
            canvasElement.width, canvasElement.height
          );
          
          canvasCtx.restore();
        });

        // Set up the camera
        camera = new Camera(videoElement, {
          onFrame: async () => {
            if (selfieSegmentation) {
              await selfieSegmentation.send({ image: videoElement });
            }
          },
          width,
          height
        });

        await camera.start();
        setIsInitialized(true);
        if (onLoad) onLoad();

      } catch (e) {
        console.error("Error initializing background blur:", e);
        const error = e instanceof Error ? e : new Error(String(e));
        setError(error);
        if (onError) onError(error);
      }
    };

    // Initialize the blur effect
    initializeBlur();

    // Cleanup
    return () => {
      if (camera) {
        camera.stop();
      }
    };
  }, [blurAmount, onLoad, onError, width, height, mirrored]); // Add mirrored to dependency array

  return (
    <div className={`relative ${className}`}>
      {error && (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center text-red-400 text-sm p-4 z-10">
          Background blur failed to initialize: {error.message}
        </div>
      )}
      
      <video 
        ref={videoRef} 
        className="hidden"
        playsInline
      />
      
      <canvas 
        ref={canvasRef} 
        width={width} 
        height={height}
        className={`w-full h-full object-cover ${className}`}
      />
    </div>
  );
};